import { useMemo } from 'react';
import { EnterpriseStructure, OwnershipType } from '@brighte/validation';
import { getEnumKey } from '@components/StepNavigator/helper';
import { CustomerType } from '../../graphql';
import { useApplicationData } from '../../provider';
import { useOpenBankingSteps } from '../../provider/OpenBankingStepsProvider';
import { isJointApplicant, pascalToCamelCase } from '../../utils';
import {
  generateBusinessStepsVariants,
  generateSteps,
  jointOpenBankingResidentialSteps,
  jointResidentialSteps,
  primaryOpenBankingResidentialSteps,
  primaryResidentialCashPaymentSteps,
  primaryResidentialSteps,
} from './constants';

export const determineApplicationSteps = (
  customerType: CustomerType,
  isCashApplication?: boolean,
  isOpenBankingConsentComplete?: boolean,
  entityStructure?: EnterpriseStructure,
  ownershipType?: OwnershipType,
) => {
  const isJoint = isJointApplicant();

  switch (true) {
    case customerType === CustomerType.Business:
      return generateBusinessStepsVariants(entityStructure, ownershipType);
    case isJoint:
      return isOpenBankingConsentComplete ? jointOpenBankingResidentialSteps : jointResidentialSteps;
    case isCashApplication:
      return primaryResidentialCashPaymentSteps;
    case isOpenBankingConsentComplete:
      return primaryOpenBankingResidentialSteps;
    default:
      return primaryResidentialSteps;
  }
};

export function useGetSteps() {
  const { quote, Finance, BusinessDetails, OwnershipDetails } = useApplicationData([
    'quote',
    'Finance',
    'BusinessDetails',
    'OwnershipDetails',
  ]);
  const { isOpenBankingConsentComplete } = useOpenBankingSteps();
  const customerType = quote?.financialProduct.customerType ?? CustomerType.Residential;

  const isCashApplication = Finance?.payment?.isCashApplication;

  const structure = BusinessDetails?.enterprise?.structure;
  const structureProp = structure ? pascalToCamelCase(getEnumKey(structure, EnterpriseStructure)) : undefined;
  const ownershipType = structureProp ? OwnershipDetails?.enterprise?.[structureProp]?.ownershipType : undefined;

  return useMemo(
    () =>
      generateSteps(
        determineApplicationSteps(
          customerType,
          isCashApplication,
          isOpenBankingConsentComplete,
          structure,
          ownershipType,
        ),
      ),
    [customerType, isCashApplication, isOpenBankingConsentComplete, structure, ownershipType],
  );
}
